import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format, parse, isValid } from "date-fns";
import { Loader2, ExternalLink, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Calendar, FileText, Building2, Coins, Target, BarChart3, ArrowUpRight, ArrowDownRight, Minus, LineChart, RefreshCw, Download } from "lucide-react";
import { generateAdviserPresentationPdf } from "@/lib/adviserPresentationPdf";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { PortfolioAllocationTabs } from "@/components/PortfolioAllocationTabs";

interface AdviserAssistContent {
  last_month_commentary: string | null;
  latest_commentary: string | null;
  performance_monitor_url: string | null;
  last_month_commentary_date: string | null;
  latest_commentary_date: string | null;
  updated_at: string;
}

interface PerformanceData {
  portfolio: string;
  mtd: string;
  qtd: string;
  ytd: string;
  oneYear: string;
  afiBenchmark: string;
  vsAfiMtd: string;
  vsAfiQtd: string;
  vsAfiYtd: string;
  vsAfi1Y: string;
  internalBenchmark: string;
  vsIntMtd: string;
  vsIntQtd: string;
  vsIntYtd: string;
  vsInt1Y: string;
}

interface FundPerformance {
  rank: number;
  name: string;
  return: string;
}

interface TradeLogEntry {
  date: string;
  rawDate: string;
  manager: string;
  fundName: string;
  fundClass: string;
  isin: string;
  p1: string;
  p2: string;
  p3: string;
  p4: string;
  p5: string;
}

interface MeetingLogEntry {
  date: string;
  topic: string;
}

interface PositioningData {
  sector: string;
  i: { position: string; value: string };
  ii: { position: string; value: string };
  iii: { position: string; value: string };
  iv: { position: string; value: string };
  v: { position: string; value: string };
}

interface GraphDataPoint {
  date: string;
  portfolio_1: number | null;
  portfolio_2: number | null;
  portfolio_3: number | null;
  portfolio_4: number | null;
  portfolio_5: number | null;
  benchmark_1: number | null;
  benchmark_2: number | null;
  benchmark_3: number | null;
  benchmark_4: number | null;
  benchmark_5: number | null;
}

const formatDateForDisplay = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const parsed = parse(dateStr, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : dateStr;
};

// Static fallback data - used when database has no data
// Data Source: Static fallback (from original PDF)
const fallbackPerformanceP: PerformanceData[] = [
  { portfolio: "CBAM P1", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "N/A", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM I", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM P2", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Cautious", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM II", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM P3", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Balanced", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM III", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM P4", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Aggressive/Balanced 50:50", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM IV", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM P5", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Aggressive", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM V", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
];

// Data Source: Static fallback (from original PDF)
const fallbackPerformanceR: PerformanceData[] = [
  { portfolio: "CBAM R1", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "N/A", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM I", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM R2", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Cautious", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM II", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM R3", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Balanced", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM III", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM R4", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Aggressive/Balanced 50:50", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM IV", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
  { portfolio: "CBAM R5", mtd: "--", qtd: "--", ytd: "--", oneYear: "--", afiBenchmark: "AFI Aggressive", vsAfiMtd: "--", vsAfiQtd: "--", vsAfiYtd: "--", vsAfi1Y: "--", internalBenchmark: "CBAM V", vsIntMtd: "--", vsIntQtd: "--", vsIntYtd: "--", vsInt1Y: "--" },
];

// Data Source: Static fallback (from original PDF) - now loaded from presentation_fund_performance table
const fallbackBestPerformingFundsP: FundPerformance[] = [
  { rank: 1, name: "Ninety One Global Gold", return: "14.49%" },
  { rank: 2, name: "Jupiter Merian Global Equity Absolute Return", return: "3.19%" },
  { rank: 3, name: "Lazard Global Listed Infrastructure Equity", return: "2.92%" },
  { rank: 4, name: "T. Rowe Price US Smaller Companies Equity", return: "2.61%" },
  { rank: 5, name: "Artemis SmartGARP European Equity", return: "2.21%" },
];

const fallbackWorstPerformingFundsP: FundPerformance[] = [
  { rank: 1, name: "EF 8AM Focussed", return: "-5.15%" },
  { rank: 2, name: "Fidelity Index Emerging Markets", return: "-3.12%" },
  { rank: 3, name: "Artemis SmartGARP Global Emerging Markets Equity", return: "-2.45%" },
  { rank: 4, name: "Fidelity Index US", return: "-1.76%" },
  { rank: 5, name: "Fidelity Index Japan", return: "-1.39%" },
];

const fallbackBestPerformingFundsR: FundPerformance[] = [
  { rank: 1, name: "Ninety One Global Gold I Acc GBP", return: "14.49%" },
  { rank: 2, name: "L&G Global Infrastructure Index I Acc", return: "2.17%" },
  { rank: 3, name: "Vanguard US Government Bond Index Hedged", return: "0.59%" },
  { rank: 4, name: "L&G Global Real Estate Dividend Index I Acc", return: "0.38%" },
  { rank: 5, name: "Vanguard UK Short-Term Investment Grade Bond", return: "0.32%" },
];

const fallbackWorstPerformingFundsR: FundPerformance[] = [
  { rank: 1, name: "Pictet China Index I GBP", return: "-3.39%" },
  { rank: 2, name: "Fidelity Index Emerging Markets P Acc", return: "-3.12%" },
  { rank: 3, name: "Fidelity Index Pacific ex Japan P Acc", return: "-2.59%" },
  { rank: 4, name: "Fidelity Index US P", return: "-1.76%" },
  { rank: 5, name: "Fidelity Index Japan P Acc", return: "-1.39%" },
];



const getPositionColor = (position: string) => {
  switch (position) {
    case "OVERWEIGHT":
      return "bg-emerald-500 text-white";
    case "POSITIVE":
      return "bg-green-500 text-white";
    case "NEUTRAL":
      return "bg-slate-400 text-white";
    case "NEGATIVE":
      return "bg-orange-500 text-white";
    case "UNDERWEIGHT":
      return "bg-red-500 text-white";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getReturnColor = (returnStr: string) => {
  const value = parseFloat(returnStr.replace('%', ''));
  if (value > 0) return "text-emerald-600 dark:text-emerald-400";
  if (value < 0) return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
};

const getReturnIcon = (returnStr: string) => {
  const value = parseFloat(returnStr.replace('%', ''));
  if (value > 0) return <ArrowUpRight className="h-4 w-4 text-emerald-500" />;
  if (value < 0) return <ArrowDownRight className="h-4 w-4 text-red-500" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const hasPerformanceValues = (rows: PerformanceData[]) =>
  rows.some(row => [row.mtd, row.qtd, row.ytd, row.oneYear].some(value => value && value !== '--'));

// Chart colors for portfolios and benchmarks
const chartColors = {
  portfolio: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'],
  benchmark: ['#6ee7b7', '#93c5fd', '#fcd34d', '#fca5a5', '#c4b5fd'],
};

const AdviserClientPresentation = () => {
  const [content, setContent] = useState<AdviserAssistContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    commentary: true,
    performance: true,
    graphs: true,
    fundPerformance: true,
    tradelog: true,
    positioning: true,
    meetings: true,
  });

  // Database-loaded data states
  const [tradeLog, setTradeLog] = useState<TradeLogEntry[]>([]);
  const [meetingLog, setMeetingLog] = useState<MeetingLogEntry[]>([]);
  const [positioningData, setPositioningData] = useState<PositioningData[]>([]);
  const [allFundData, setAllFundData] = useState<{rank: number; fund_name: string; mtd_return: string; portfolio_range: string; performance_type: string; period: string}[]>([]);
  const [fundPerfPeriod, setFundPerfPeriod] = useState("1M");
  const [bestPerformingFundsP, setBestPerformingFundsP] = useState<FundPerformance[]>(fallbackBestPerformingFundsP);
  const [worstPerformingFundsP, setWorstPerformingFundsP] = useState<FundPerformance[]>(fallbackWorstPerformingFundsP);
  const [bestPerformingFundsR, setBestPerformingFundsR] = useState<FundPerformance[]>(fallbackBestPerformingFundsR);
  const [worstPerformingFundsR, setWorstPerformingFundsR] = useState<FundPerformance[]>(fallbackWorstPerformingFundsR);
  const [graphDataP1Y, setGraphDataP1Y] = useState<GraphDataPoint[]>([]);
  const [graphDataP3Y, setGraphDataP3Y] = useState<GraphDataPoint[]>([]);
  const [graphDataR1Y, setGraphDataR1Y] = useState<GraphDataPoint[]>([]);
  const [graphDataR3Y, setGraphDataR3Y] = useState<GraphDataPoint[]>([]);
  const [portfolioPerformanceP, setPortfolioPerformanceP] = useState<PerformanceData[]>(fallbackPerformanceP);
  const [portfolioPerformanceR, setPortfolioPerformanceR] = useState<PerformanceData[]>(fallbackPerformanceR);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tradeLogFilter, setTradeLogFilter] = useState<string>("1Y");
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const latestData = await loadDatabaseData();
      const exportPerformanceP = latestData?.performanceP || portfolioPerformanceP;
      const exportPerformanceR = latestData?.performanceR || portfolioPerformanceR;
      if (!hasPerformanceValues(exportPerformanceP) || !hasPerformanceValues(exportPerformanceR)) {
        toast.error("Performance data is still loading or unavailable. Please refresh data, then try the PDF again.");
        return;
      }

      const now = new Date();
      const cutoff = tradeLogFilter === "3M" ? new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
        : tradeLogFilter === "6M" ? new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
        : tradeLogFilter === "1Y" ? new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
        : null;
      const filteredTrades = cutoff ? tradeLog.filter(t => new Date(t.rawDate) >= cutoff) : tradeLog;
      const tradeRangeLabel = tradeLogFilter === "ALL" ? "All available trades"
        : tradeLogFilter === "1Y" ? "Last 12 months"
        : tradeLogFilter === "6M" ? "Last 6 months"
        : "Last 3 months";
      const fundPeriodLabel = fundPerfPeriod === "1M" ? "Month to Date"
        : fundPerfPeriod === "3M" ? "Quarter to Date" : "Year to Date";
      const fundPerformancePeriods = ["1M", "3M", "YTD"].map((period) => {
        const filtered = allFundData.filter(f => f.period === period);
        const toRows = (range: string, type: string) => filtered
          .filter(f => f.portfolio_range === range && f.performance_type === type)
          .map(f => ({ rank: f.rank, name: f.fund_name, return: f.mtd_return }));
        return {
          label: period === "1M" ? "Month to Date" : period === "3M" ? "Quarter to Date" : "Year to Date",
          bestP: toRows("P", "best"),
          worstP: toRows("P", "worst"),
          bestR: toRows("R", "best"),
          worstR: toRows("R", "worst"),
        };
      });

      await generateAdviserPresentationPdf({
        updateDate,
        lastMonthCommentary: {
          date: formatDateForDisplay(content?.last_month_commentary_date || null) || "",
          text: content?.last_month_commentary || "",
        },
        latestCommentary: {
          date: formatDateForDisplay(content?.latest_commentary_date || null) || "",
          text: content?.latest_commentary || "",
        },
        performanceP: exportPerformanceP,
        performanceR: exportPerformanceR,
        fundPeriodLabel,
        bestP: bestPerformingFundsP, worstP: worstPerformingFundsP,
        bestR: bestPerformingFundsR, worstR: worstPerformingFundsR,
        fundPerformancePeriods,
        tradeLogRangeLabel: tradeRangeLabel,
        trades: filteredTrades,
        positioning: positioningData,
      });
      toast.success("PDF generated");
    } catch (e: any) {
      console.error(e);
      toast.error("Failed to generate PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const forceRefreshData = async () => {
    setIsRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke('adviser-presentation-data', {
        body: { action: 'sync' }
      });
      if (error) {
        console.error('Error forcing data refresh:', error);
      } else {
        console.log('Force refresh triggered successfully');
        await loadDatabaseData();
      }
    } catch (error) {
      console.error('Error forcing data refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadContent();
    checkAndSyncData();
  }, []);

  // Filter fund performance by selected period
  useEffect(() => {
    if (allFundData.length === 0) return;
    const filtered = allFundData.filter(f => f.period === fundPerfPeriod);
    const bestP = filtered.filter(f => f.portfolio_range === 'P' && f.performance_type === 'best');
    const worstP = filtered.filter(f => f.portfolio_range === 'P' && f.performance_type === 'worst');
    const bestR = filtered.filter(f => f.portfolio_range === 'R' && f.performance_type === 'best');
    const worstR = filtered.filter(f => f.portfolio_range === 'R' && f.performance_type === 'worst');
    setBestPerformingFundsP(bestP.length > 0 ? bestP.map(f => ({ rank: f.rank, name: f.fund_name, return: f.mtd_return })) : []);
    setWorstPerformingFundsP(worstP.length > 0 ? worstP.map(f => ({ rank: f.rank, name: f.fund_name, return: f.mtd_return })) : []);
    setBestPerformingFundsR(bestR.length > 0 ? bestR.map(f => ({ rank: f.rank, name: f.fund_name, return: f.mtd_return })) : []);
    setWorstPerformingFundsR(worstR.length > 0 ? worstR.map(f => ({ rank: f.rank, name: f.fund_name, return: f.mtd_return })) : []);
  }, [allFundData, fundPerfPeriod]);

  // Check if data needs refreshing (hasn't been synced in the last hour)
  const checkAndSyncData = async () => {
    try {
      // Check when presentation data was last refreshed
      const { data: refreshStatus } = await supabase
        .from('data_refresh_status')
        .select('last_refresh')
        .eq('data_source', 'adviser_presentation')
        .maybeSingle();

      const lastRefresh = refreshStatus?.last_refresh 
        ? new Date(refreshStatus.last_refresh) 
        : null;
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

      // If no refresh record or last refresh was more than 1 hour ago, trigger sync
      if (!lastRefresh || lastRefresh < oneHourAgo) {
        console.log('Triggering presentation data sync (last sync:', lastRefresh || 'never', ')');
        
        // Call the edge function to sync data from Google Sheets
        const { error } = await supabase.functions.invoke('adviser-presentation-data', {
          body: { action: 'sync' }
        });

        if (error) {
          console.error('Error syncing presentation data:', error);
        } else {
          console.log('Presentation data sync triggered successfully');
        }
      } else {
        console.log('Presentation data is fresh (last sync:', lastRefresh, ')');
      }

      // Load the data regardless of whether we triggered a sync
      await loadDatabaseData();
    } catch (error) {
      console.error('Error checking/syncing presentation data:', error);
      // Still try to load existing data
      await loadDatabaseData();
    }
  };

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('adviser_assist_content')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error loading content:', error);
        return;
      }

      setContent(data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDatabaseData = async (): Promise<{ performanceP: PerformanceData[]; performanceR: PerformanceData[] } | null> => {
    setIsDataLoading(true);
    let loadedPerformanceP: PerformanceData[] | null = null;
    let loadedPerformanceR: PerformanceData[] | null = null;
    try {
      // Load trade log from ba_trades table
      const { data: tradeData } = await supabase
        .from('ba_trades')
        .select('*')
        .order('trade_date', { ascending: false });

      // Load fund records for lookup
      const { data: fundsData } = await supabase
        .from('ba_cbam_funds')
        .select('id, fund_name, fund_manager, fund_class, isin');
      const fundMap = new Map((fundsData || []).map((f: any) => [f.id, f]));

      const parseOverride = (s: string | null | undefined) => {
        if (!s) return { manager: "", fund: "", klass: "" };
        const parts = s.split(".").map(p => p.trim());
        return { manager: parts[0] || "", fund: parts[1] || "", klass: parts[2] || "" };
      };

      if (tradeData && tradeData.length > 0) {
        setTradeLog(tradeData.map(t => {
          const sign = t.direction === 'SELL' ? -1 : 1;
          const f: any = t.fund_id ? fundMap.get(t.fund_id) : null;
          const parsed = parseOverride(t.fund_name_override);
          const manager = f?.fund_manager || parsed.manager || "—";
          const fundName = f?.fund_name || parsed.fund || t.fund_name_override || 'Unknown Fund';
          const fundClass = f?.fund_class || parsed.klass || "—";
          const isin = f?.isin || "—";
          const formatPct = (included: boolean | null, pct: number | null) => {
            if (!included || pct === null || pct === 0) return '0.00%';
            return `${(pct * sign).toFixed(2)}%`;
          };
          return {
            date: format(new Date(t.trade_date), 'dd/MM/yyyy'),
            rawDate: t.trade_date,
            manager,
            fundName,
            fundClass,
            isin,
            p1: formatPct(t.portfolio_1_included, t.portfolio_1_pct),
            p2: formatPct(t.portfolio_2_included, t.portfolio_2_pct),
            p3: formatPct(t.portfolio_3_included, t.portfolio_3_pct),
            p4: formatPct(t.portfolio_4_included, t.portfolio_4_pct),
            p5: formatPct(t.portfolio_5_included, t.portfolio_5_pct),
          };
        }));
      }

      // Load meeting log from database (newest to oldest)
      const { data: meetingData } = await supabase
        .from('presentation_meeting_log')
        .select('*')
        .order('meeting_date', { ascending: false });

      if (meetingData && meetingData.length > 0) {
        setMeetingLog(meetingData.map(m => ({
          date: format(new Date(m.meeting_date), 'dd/MM/yyyy'),
          topic: m.topic,
        })));
      }

      // Load fund performance from database
      const { data: fundData } = await supabase
        .from('presentation_fund_performance')
        .select('*')
        .order('rank', { ascending: true });

      if (fundData && fundData.length > 0) {
        setAllFundData(fundData.map(f => ({ ...f, period: f.period || '1M' })));
      }

      // Load positioning data from the same source as /partner/portfoliomanagement/p
      try {
        const { loadPPositioningRows } = await import('@/lib/pBenchmarkPositioning');
        const posRows = await loadPPositioningRows();
        if (posRows.length > 0) setPositioningData(posRows);
      } catch (e) {
        console.error('Failed to load P benchmark positioning', e);
      }


      // Load graph data from database - fetch each type separately to avoid 1000 row limit
      const formatGraphData = (data: { data_date: string; portfolio_1: number | null; portfolio_2: number | null; portfolio_3: number | null; portfolio_4: number | null; portfolio_5: number | null; benchmark_1: number | null; benchmark_2: number | null; benchmark_3: number | null; benchmark_4: number | null; benchmark_5: number | null }[]): GraphDataPoint[] => 
        data.map(g => ({
          date: format(new Date(g.data_date), 'MMM yyyy'),
          portfolio_1: g.portfolio_1 ? Number(g.portfolio_1) : null,
          portfolio_2: g.portfolio_2 ? Number(g.portfolio_2) : null,
          portfolio_3: g.portfolio_3 ? Number(g.portfolio_3) : null,
          portfolio_4: g.portfolio_4 ? Number(g.portfolio_4) : null,
          portfolio_5: g.portfolio_5 ? Number(g.portfolio_5) : null,
          benchmark_1: g.benchmark_1 ? Number(g.benchmark_1) : null,
          benchmark_2: g.benchmark_2 ? Number(g.benchmark_2) : null,
          benchmark_3: g.benchmark_3 ? Number(g.benchmark_3) : null,
          benchmark_4: g.benchmark_4 ? Number(g.benchmark_4) : null,
          benchmark_5: g.benchmark_5 ? Number(g.benchmark_5) : null,
        }));

      // Helper to fetch all rows using range-based pagination (PostgREST caps at 1000)
      const fetchAllGraphData = async (graphType: string) => {
        const allRows: any[] = [];
        const pageSize = 1000;
        let offset = 0;
        let hasMore = true;
        while (hasMore) {
          const { data } = await supabase
            .from('presentation_graph_data')
            .select('*')
            .eq('graph_type', graphType)
            .order('data_date', { ascending: true })
            .range(offset, offset + pageSize - 1);
          if (data && data.length > 0) {
            allRows.push(...data);
            offset += data.length;
            hasMore = data.length === pageSize;
          } else {
            hasMore = false;
          }
        }
        return allRows;
      };

      const [p1yData, p3yData, r1yData, r3yData] = await Promise.all([
        fetchAllGraphData('P_1Y'),
        fetchAllGraphData('P_3Y'),
        fetchAllGraphData('R_1Y'),
        fetchAllGraphData('R_3Y'),
      ]);

      if (p1yData.length) setGraphDataP1Y(formatGraphData(p1yData));
      if (p3yData.length) setGraphDataP3Y(formatGraphData(p3yData));
      if (r1yData.length) setGraphDataR1Y(formatGraphData(r1yData));
      if (r3yData.length) setGraphDataR3Y(formatGraphData(r3yData));

      // Load portfolio performance data from cache
      const { data: perfData } = await supabase
        .from('portfolio_performance_cache')
        .select('*')
        .order('portfolio');

      if (perfData && perfData.length > 0) {
        const afiBenchmarks: Record<string, string> = {
          'P1': 'N/A', 'P2': 'AFI Cautious', 'P3': 'AFI Balanced',
          'P4': 'AFI Aggressive/Balanced 50:50', 'P5': 'AFI Aggressive',
          'R1': 'N/A', 'R2': 'AFI Cautious', 'R3': 'AFI Balanced',
          'R4': 'AFI Aggressive/Balanced 50:50', 'R5': 'AFI Aggressive'
        };
        const internalBenchmarks: Record<string, string> = {
          'P1': 'CBAM I', 'P2': 'CBAM II', 'P3': 'CBAM III', 'P4': 'CBAM IV', 'P5': 'CBAM V',
          'R1': 'CBAM I', 'R2': 'CBAM II', 'R3': 'CBAM III', 'R4': 'CBAM IV', 'R5': 'CBAM V'
        };

        // Get data for each period
        const mtdData = perfData.filter(p => p.period === '1m');
        const ytdData = perfData.filter(p => p.period === 'YTD');
        const oneYrData = perfData.filter(p => p.period === '1Yr');
        
        // QTD logic: In Q1 (Jan-Mar), QTD = YTD since both start Jan 1
        // In other quarters, we'd need separate QTD data, but for now use YTD in Q1
        const currentMonth = new Date().getMonth(); // 0 = January
        const isQ1 = currentMonth >= 0 && currentMonth <= 2; // Jan, Feb, Mar
        const qtdData = isQ1 ? ytdData : perfData.filter(p => p.period === '3m');
        
        const buildPerfData = (prefix: string): PerformanceData[] => {
          const portfolios = ['1', '2', '3', '4', '5'];
          return portfolios.map(num => {
            const code = `${prefix}${num}`;
            const mtd = mtdData.find(p => p.portfolio === code);
            const qtd = qtdData.find(p => p.portfolio === code);
            const ytd = ytdData.find(p => p.portfolio === code);
            const oneYr = oneYrData.find(p => p.portfolio === code);
            return {
              portfolio: `CBAM ${code}`,
              mtd: mtd?.portfolio_return || '--',
              qtd: qtd?.portfolio_return || '--',
              ytd: ytd?.portfolio_return || '--',
              oneYear: oneYr?.portfolio_return || '--',
              afiBenchmark: afiBenchmarks[code] || '--',
              vsAfiMtd: afiBenchmarks[code] === 'N/A' ? 'N/A' : (mtd?.excess2_return || '--'),
              vsAfiQtd: afiBenchmarks[code] === 'N/A' ? 'N/A' : (qtd?.excess2_return || '--'),
              vsAfiYtd: afiBenchmarks[code] === 'N/A' ? 'N/A' : (ytd?.excess2_return || '--'),
              vsAfi1Y: afiBenchmarks[code] === 'N/A' ? 'N/A' : (oneYr?.excess2_return || '--'),
              internalBenchmark: internalBenchmarks[code] || '--',
              vsIntMtd: mtd?.excess_return || '--',
              vsIntQtd: qtd?.excess_return || '--',
              vsIntYtd: ytd?.excess_return || '--',
              vsInt1Y: oneYr?.excess_return || '--',
            };
          });
        };

        loadedPerformanceP = buildPerfData('P');
        loadedPerformanceR = buildPerfData('R');
        setPortfolioPerformanceP(loadedPerformanceP);
        setPortfolioPerformanceR(loadedPerformanceR);
      }

      // Allocation is now loaded via useAssetAllocation hook
      return loadedPerformanceP && loadedPerformanceR
        ? { performanceP: loadedPerformanceP, performanceR: loadedPerformanceR }
        : null;
    } catch (error) {
      console.error('Error loading database data:', error);
      return null;
    } finally {
      setIsDataLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const updateDate = content?.updated_at ? format(new Date(content.updated_at), "dd/MM/yyyy") : "N/A";

  const renderPerformanceGraph = (data: GraphDataPoint[], portfolioPrefix: string) => {
    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-[600px] bg-muted/30 rounded-lg">
          <p className="text-muted-foreground">No graph data available. Data will be loaded from Google Sheets.</p>
        </div>
      );
    }

    // Determine which portfolios have data (not all null)
    const portfoliosWithData = [1, 2, 3, 4, 5].filter(num => 
      data.some(point => point[`portfolio_${num}` as keyof GraphDataPoint] !== null && point[`portfolio_${num}` as keyof GraphDataPoint] !== undefined)
    );

    return (
      <ResponsiveContainer width="100%" height={600}>
        <RechartsLineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" className="text-xs" />
          <YAxis className="text-xs" tickFormatter={(v) => `${v}%`} />
          <Tooltip formatter={(value: number) => [`${value?.toFixed(2)}%`, '']} />
          <Legend />
          {portfoliosWithData.map((num, idx) => (
            <Line
              key={`portfolio_${num}`}
              type="monotone"
              dataKey={`portfolio_${num}`}
              name={`${portfolioPrefix}${num}`}
              stroke={chartColors.portfolio[num - 1]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/20 to-background">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-r from-primary/10 via-primary/5 to-background py-12 border-b">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img 
                src="/images/cbam-logo.png" 
                alt="Cape Berkshire Asset Management" 
                className="h-16 w-auto"
              />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  Adviser-Client Presentation
                </h1>
                <p className="text-muted-foreground mt-1">
                  Update: {updateDate}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="default" className="gap-2" onClick={handleExportPdf} disabled={isExporting || isDataLoading}>
                <Download className={cn("h-4 w-4", (isExporting || isDataLoading) && "animate-pulse")} />
                {isExporting ? "Generating PDF..." : isDataLoading ? "Loading data..." : "Download PDF"}
              </Button>
              <Button variant="outline" className="gap-2" onClick={forceRefreshData} disabled={isRefreshing}>
                <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
                {isRefreshing ? "Refreshing..." : "Refresh Data"}
              </Button>
              {content?.performance_monitor_url && (
                <Button asChild variant="outline" className="gap-2">
                  <a href={content.performance_monitor_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    Performance Monitor
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8 space-y-8">
        {/* Commentary Section - Data Source: Supabase adviser_assist_content table */}
        <Collapsible open={expandedSections.commentary} onOpenChange={() => toggleSection('commentary')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle>Director's Commentary</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - adviser_assist_content table</p>
                  </div>
                </div>
                {expandedSections.commentary ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                {/* Last Month Commentary */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {formatDateForDisplay(content?.last_month_commentary_date) || "Last Month"}
                    </Badge>
                    <h3 className="font-semibold text-lg">Last Month's Commentary</h3>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 rounded-lg p-4">
                    {content?.last_month_commentary?.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-3 last:mb-0 text-muted-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    )) || <p className="text-muted-foreground italic">No commentary available</p>}
                  </div>
                </div>

                <Separator />

                {/* Latest Commentary */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge>
                      {formatDateForDisplay(content?.latest_commentary_date) || "Latest"}
                    </Badge>
                    <h3 className="font-semibold text-lg">Latest Commentary</h3>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none bg-muted/30 rounded-lg p-4">
                    {content?.latest_commentary?.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-3 last:mb-0 text-muted-foreground leading-relaxed">
                        {paragraph}
                      </p>
                    )) || <p className="text-muted-foreground italic">No commentary available</p>}
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Portfolio Performance Section - Data Source: Supabase portfolio_performance_cache table */}
        <Collapsible open={expandedSections.performance} onOpenChange={() => toggleSection('performance')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <BarChart3 className="h-5 w-5 text-emerald-500" />
                  </div>
                  <div>
                    <CardTitle>Portfolio Performance</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - portfolio_performance_cache table (via Google Sheets refresh)</p>
                  </div>
                </div>
                {expandedSections.performance ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                {/* CBAM P Performance */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Badge variant="outline" className="bg-primary/10">CBAM P</Badge>
                    Portfolio Range Performance
                  </h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead rowSpan={2} className="align-middle">Portfolio</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">Portfolio Return</TableHead>
                          <TableHead rowSpan={2} className="align-middle">AFI BM</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">vs AFI</TableHead>
                          <TableHead rowSpan={2} className="align-middle">Int. BM</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">vs Internal</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {portfolioPerformanceP.map((row) => (
                          <TableRow key={row.portfolio}>
                            <TableCell className="font-medium">{row.portfolio}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.mtd))}>{row.mtd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.qtd))}>{row.qtd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.ytd))}>{row.ytd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.oneYear))}>{row.oneYear}</TableCell>
                            <TableCell className="text-muted-foreground text-xs">{row.afiBenchmark}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiMtd))}>{row.vsAfiMtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiQtd))}>{row.vsAfiQtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiYtd))}>{row.vsAfiYtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfi1Y))}>{row.vsAfi1Y}</TableCell>
                            <TableCell className="text-muted-foreground text-xs">{row.internalBenchmark}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntMtd))}>{row.vsIntMtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntQtd))}>{row.vsIntQtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntYtd))}>{row.vsIntYtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsInt1Y))}>{row.vsInt1Y}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <Separator />

                {/* CBAM R Performance */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-500/10">CBAM R</Badge>
                    Portfolio Range Performance
                  </h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead rowSpan={2} className="align-middle">Portfolio</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">Portfolio Return</TableHead>
                          <TableHead rowSpan={2} className="align-middle">AFI BM</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">vs AFI</TableHead>
                          <TableHead rowSpan={2} className="align-middle">Int. BM</TableHead>
                          <TableHead colSpan={4} className="text-center border-b">vs Internal</TableHead>
                        </TableRow>
                        <TableRow>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                          <TableHead className="text-right text-xs">MTD</TableHead>
                          <TableHead className="text-right text-xs">QTD</TableHead>
                          <TableHead className="text-right text-xs">YTD</TableHead>
                          <TableHead className="text-right text-xs">1Y</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {portfolioPerformanceR.map((row) => (
                          <TableRow key={row.portfolio}>
                            <TableCell className="font-medium">{row.portfolio}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.mtd))}>{row.mtd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.qtd))}>{row.qtd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.ytd))}>{row.ytd}</TableCell>
                            <TableCell className={cn("text-right font-semibold text-sm", getReturnColor(row.oneYear))}>{row.oneYear}</TableCell>
                            <TableCell className="text-muted-foreground text-xs">{row.afiBenchmark}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiMtd))}>{row.vsAfiMtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiQtd))}>{row.vsAfiQtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfiYtd))}>{row.vsAfiYtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsAfi1Y))}>{row.vsAfi1Y}</TableCell>
                            <TableCell className="text-muted-foreground text-xs">{row.internalBenchmark}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntMtd))}>{row.vsIntMtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntQtd))}>{row.vsIntQtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsIntYtd))}>{row.vsIntYtd}</TableCell>
                            <TableCell className={cn("text-right text-sm", getReturnColor(row.vsInt1Y))}>{row.vsInt1Y}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* P&R Performance Graphs Section - Data Source: Supabase presentation_graph_data table */}
        <Collapsible open={expandedSections.graphs} onOpenChange={() => toggleSection('graphs')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-indigo-500/10">
                    <LineChart className="h-5 w-5 text-indigo-500" />
                  </div>
                  <div>
                    <CardTitle>P&R Performance Graphs</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Google Sheets - P_Graph_Gen_1Y, P_Graph_Gen_3Y, R_Graph_Gen_1Y, R_Graph_Gen_3Y</p>
                  </div>
                </div>
                {expandedSections.graphs ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <Tabs defaultValue="p3y" className="space-y-4">
                  <TabsList className="grid grid-cols-4 w-full">
                    <TabsTrigger value="p1y">CBAM P (1Y)</TabsTrigger>
                    <TabsTrigger value="p3y">CBAM P (3Y)</TabsTrigger>
                    <TabsTrigger value="r1y">CBAM R (1Y)</TabsTrigger>
                    <TabsTrigger value="r3y">CBAM R (3Y)</TabsTrigger>
                  </TabsList>
                  <TabsContent value="p1y">
                    <div className="space-y-2">
                      <h4 className="font-medium">CBAM P Range - 1 Year Performance</h4>
                      {renderPerformanceGraph(graphDataP1Y, 'P')}
                    </div>
                  </TabsContent>
                  <TabsContent value="p3y">
                    <div className="space-y-2">
                      <h4 className="font-medium">CBAM P Range - 3 Year Performance</h4>
                      {renderPerformanceGraph(graphDataP3Y, 'P')}
                    </div>
                  </TabsContent>
                  <TabsContent value="r1y">
                    <div className="space-y-2">
                      <h4 className="font-medium">CBAM R Range - 1 Year Performance</h4>
                      {renderPerformanceGraph(graphDataR1Y, 'R')}
                    </div>
                  </TabsContent>
                  <TabsContent value="r3y">
                    <div className="space-y-2">
                      <h4 className="font-medium">CBAM R Range - 3 Year Performance</h4>
                      {renderPerformanceGraph(graphDataR3Y, 'R')}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Fund Performance Section - Data Source: Supabase presentation_fund_performance table */}
        <Collapsible open={expandedSections.fundPerformance} onOpenChange={() => toggleSection('fundPerformance')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                <CardTitle>Fund Performance</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - presentation_fund_performance table</p>
                  </div>
                </div>
                {expandedSections.fundPerformance ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  {[
                    { label: "Month to Date", value: "1M" },
                    { label: "Quarter to Date", value: "3M" },
                    { label: "Year to Date", value: "YTD" },
                  ].map(p => (
                    <Button
                      key={p.value}
                      size="sm"
                      variant={fundPerfPeriod === p.value ? "default" : "outline"}
                      onClick={() => setFundPerfPeriod(p.value)}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* CBAM P Funds */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">CBAM P</h3>
                    <div className="space-y-3">
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-3 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Best Performing
                        </h4>
                        <div className="space-y-2">
                          {bestPerformingFundsP.map((fund) => (
                            <div key={fund.rank} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">{fund.rank}</span>
                                <span className="truncate">{fund.name}</span>
                              </span>
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fund.return}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                          <TrendingDown className="h-4 w-4" />
                          Worst Performing
                        </h4>
                        <div className="space-y-2">
                          {worstPerformingFundsP.map((fund) => (
                            <div key={fund.rank} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{fund.rank}</span>
                                <span className="truncate">{fund.name}</span>
                              </span>
                              <span className="font-semibold text-red-600 dark:text-red-400">{fund.return}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CBAM R Funds */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">CBAM R</h3>
                    <div className="space-y-3">
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-3 flex items-center gap-2">
                          <TrendingUp className="h-4 w-4" />
                          Best Performing
                        </h4>
                        <div className="space-y-2">
                          {bestPerformingFundsR.map((fund) => (
                            <div key={fund.rank} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">{fund.rank}</span>
                                <span className="truncate">{fund.name}</span>
                              </span>
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fund.return}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                          <TrendingDown className="h-4 w-4" />
                          Worst Performing
                        </h4>
                        <div className="space-y-2">
                          {worstPerformingFundsR.map((fund) => (
                            <div key={fund.rank} className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{fund.rank}</span>
                                <span className="truncate">{fund.name}</span>
                              </span>
                              <span className="font-semibold text-red-600 dark:text-red-400">{fund.return}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Trade Log Section - Data Source: Supabase ba_trades table */}
        <Collapsible open={expandedSections.tradelog} onOpenChange={() => toggleSection('tradelog')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-violet-500/10">
                    <Building2 className="h-5 w-5 text-violet-500" />
                  </div>
                  <div>
                    <CardTitle>Trade Log</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - ba_trades table</p>
                  </div>
                </div>
                {expandedSections.tradelog ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  {[
                    { label: "3 Months", value: "3M" },
                    { label: "6 Months", value: "6M" },
                    { label: "1 Year", value: "1Y" },
                    { label: "All", value: "ALL" },
                  ].map(f => (
                    <Button
                      key={f.value}
                      variant={tradeLogFilter === f.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTradeLogFilter(f.value)}
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
                {(() => {
                  const now = new Date();
                  const cutoff = tradeLogFilter === "3M" ? new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
                    : tradeLogFilter === "6M" ? new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
                    : tradeLogFilter === "1Y" ? new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
                    : null;
                  const filtered = cutoff ? tradeLog.filter(t => new Date(t.rawDate) >= cutoff) : tradeLog;
                  return (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Manager</TableHead>
                        <TableHead>Fund</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>ISIN</TableHead>
                        <TableHead className="text-right">P1</TableHead>
                        <TableHead className="text-right">P2</TableHead>
                        <TableHead className="text-right">P3</TableHead>
                        <TableHead className="text-right">P4</TableHead>
                        <TableHead className="text-right">P5</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                            No trade log entries for this period.
                          </TableCell>
                        </TableRow>
                      ) : filtered.map((trade, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-muted-foreground text-sm">{trade.date}</TableCell>
                          <TableCell>{trade.manager}</TableCell>
                          <TableCell className="font-medium">{trade.fundName}</TableCell>
                          <TableCell className="text-sm">{trade.fundClass}</TableCell>
                          <TableCell className="text-xs font-mono">{trade.isin}</TableCell>
                          <TableCell className={cn("text-right", getReturnColor(trade.p1))}>{trade.p1}</TableCell>
                          <TableCell className={cn("text-right", getReturnColor(trade.p2))}>{trade.p2}</TableCell>
                          <TableCell className={cn("text-right", getReturnColor(trade.p3))}>{trade.p3}</TableCell>
                          <TableCell className={cn("text-right", getReturnColor(trade.p4))}>{trade.p4}</TableCell>
                          <TableCell className={cn("text-right", getReturnColor(trade.p5))}>{trade.p5}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                  );
                })()}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Positioning Section - Data Source: Supabase ba_presentation_positioning table */}
        <Collapsible open={expandedSections.positioning} onOpenChange={() => toggleSection('positioning')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Target className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle>CBAM Positioning vs Benchmark</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: P Portfolio Management – Portfolio Views Matrix (live)</p>
                  </div>
                </div>
                {expandedSections.positioning ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sector</TableHead>
                        <TableHead className="text-center">I</TableHead>
                        <TableHead className="text-center">II</TableHead>
                        <TableHead className="text-center">III</TableHead>
                        <TableHead className="text-center">IV</TableHead>
                        <TableHead className="text-center">V</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {positioningData.map((row) => (
                        <TableRow key={row.sector}>
                          <TableCell className="font-medium">{row.sector}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={cn("text-xs", getPositionColor(row.i.position))}>{row.i.position}</Badge>
                              <span className={cn("text-xs", getReturnColor(row.i.value))}>{row.i.value}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={cn("text-xs", getPositionColor(row.ii.position))}>{row.ii.position}</Badge>
                              <span className={cn("text-xs", getReturnColor(row.ii.value))}>{row.ii.value}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={cn("text-xs", getPositionColor(row.iii.position))}>{row.iii.position}</Badge>
                              <span className={cn("text-xs", getReturnColor(row.iii.value))}>{row.iii.value}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={cn("text-xs", getPositionColor(row.iv.position))}>{row.iv.position}</Badge>
                              <span className={cn("text-xs", getReturnColor(row.iv.value))}>{row.iv.value}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col items-center gap-1">
                              <Badge className={cn("text-xs", getPositionColor(row.v.position))}>{row.v.position}</Badge>
                              <span className={cn("text-xs", getReturnColor(row.v.value))}>{row.v.value}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Medium Scan & Factsheets - Data Source: asset_allocation_cache table (same as portfolio pages) */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Coins className="h-5 w-5 text-cyan-500" />
              </div>
              <div>
                <CardTitle>Portfolio Asset Allocation</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - asset_allocation_cache table (same as portfolio pages)</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <PortfolioAllocationTabs />
          </CardContent>
        </Card>

        {/* Meeting Log Section - Temporarily hidden, will be re-enabled in future */}
        {/* <Collapsible open={expandedSections.meetings} onOpenChange={() => toggleSection('meetings')}>
          <Card className="overflow-hidden">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-pink-500/10">
                    <Calendar className="h-5 w-5 text-pink-500" />
                  </div>
                  <div>
                    <CardTitle>Meeting Log</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Data Source: Supabase - presentation_meeting_log table</p>
                  </div>
                </div>
                {expandedSections.meetings ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="grid gap-2">
                  {meetingLog.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      No meeting entries yet. Add entries via the Meeting Log Editor in the Adviser Assist Editor.
                    </div>
                  ) : meetingLog.map((meeting, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <span className="text-sm text-muted-foreground w-24 shrink-0">{meeting.date}</span>
                      <span className="text-sm">{meeting.topic}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible> */}

      </div>
    </div>
  );
};

export default AdviserClientPresentation;
